import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Box,
  Chip,
  Typography,
  Modal,
  Button,
  IconButton,
  Paper,
  TablePagination,
  Snackbar,
} from "@mui/material";
import AlertMessage from "../common/AlertMessage";
import { copyPackageDetails, getStorageValue, setStorageValue } from "../../utils/helper";

import {
  tableHeadersAll,
  tableHeadersAction,
  statusHolder,
} from "../../data/PackageListing";
import { useNavigate } from "react-router";
import PackageListings from "../../utils/services/PackageListing.api";
import { validateDate } from "../../utils/helper";
import NoDataFound from "../common/NoDataFound";
import deleteIcon from "../../assets/delete_icon.svg";
import Loaders from "../common/Loaders";

const PackageListingTable = ({
  type,
  searchParams,
  filterParams,
  onDeleteSuccess,
  acceptedBy,
  requestedBy,
  originPage,
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [pgNo, setPgNo] = useState(0);
  const [pgSize, setPgSize] = useState(localStorage.getItem("PageSize") ? localStorage.getItem("PageSize") : 10);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const CurrentLatitude = getStorageValue("latitude");
  const CurrentLongitude = getStorageValue("longitude");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [itemDetails, setItemDetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const roleId = (getStorageValue("roleId") === "2" && originPage === "MyDelivery" && type === 1) 
      ? 1
      : (acceptedBy || requestedBy) ? "" : parseInt(getStorageValue("roleId"));
  const navigate = useNavigate();

  const handleDeleteClick = (packagePin, title) => {
    setSelectedPackageId(packagePin);
    setItemDetails((prevDetails) => ({
      ...prevDetails,
      title: title,
    }));
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmDialogOpen(false);
    setLoader(true);

    try {
      const userId = parseInt(getStorageValue("userId"));
      const response = await PackageListings.DeleteJobById(
        selectedPackageId,
        userId
      );

      if (response.status === "success") {
        setAlertMessage("Job Deleted Successfully.");
        setAlertSeverity("success");
        await getListingData();

        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      } else {
        setAlertMessage(response.message || "Failed to delete package");
        setAlertSeverity("error");
      }
    } catch (err) {
      setAlertMessage("An error occurred while deleting the package");
      setAlertSeverity("error");
    } finally {
      setLoader(false);
      setOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const handleCopyDetails = async (packagePin) => {
    try {
      const response = await PackageListings.getPackageDetailsbyId(packagePin);
      if (response.status === "success") {
        const itemDetails = response.data;
        setItemDetails(itemDetails);
        copyPackageDetails(itemDetails, handleSnackbarOpen)
      } else {
        console.error("Failed to fetch package details:", response.message);
      }
    } catch (error) {
      setAlertMessage("Failed to copy package details");
      setAlertSeverity("error");
      setOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setCopySuccess(false);
  };

  const handleSnackbarOpen = () => {
    setCopySuccess(true);
  };

  // Headers to display
  var displayHeaders =
    type === 0
      ? tableHeadersAll
      : (type === 1 || type === 5) && originPage !== "MyDelivery"
      ? tableHeadersAction
      : tableHeadersAll.slice(0, 6);

  // API Call to get Table data
  const getListingData = async () => {
    if (!localStorage.getItem("SearchParamsChanged")) {
      setLoading(true);
    }
    const accepted = (getStorageValue("roleId") === "2" && originPage === "MyDelivery" && type === 1) ? undefined : acceptedBy;

    const response = await PackageListings.getPackageListing(
      roleId,
      type,
      pgNo + 1,
      pgSize,
      searchParams,
      filterParams,
      CurrentLatitude,
      CurrentLongitude,
      accepted,
      requestedBy
    );
    if (response.status !== "success") {
      localStorage.setItem("SearchParamsChanged", "");
      setLoading(false);
      if (response.error.response.status === 401) {
        navigate("/")
      } else {
        setAlertSeverity(response.status);
        setAlertMessage(
          response?.error?.response?.data?.title
            ? response?.error?.response?.data?.title
            : response.message
        );
        setOpen(true);
      }
    } else {
      localStorage.setItem("SearchParamsChanged", "");
      setLoading(false);
      setData(response.data.packageList);
      setTotalData(response.data.count);
      setAlertSeverity(response.status);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // PAGINATION FUNCTIONS
  const handlePageChange = (e, newPage) => {
    setPgNo(newPage);
    if(!searchParams) {
      setStorageValue("PageNumber", newPage);
    }
  };

  const handleRowsChange = (e) => {
    setPgNo(0);
    setPgSize(e.target.value);
    localStorage.setItem("PageSize", e.target.value);
  };

  const handleNavigate = (packagePin) => {
    navigate(`/packagedetails/${packagePin}`, {
      state: {
        tab: type,
        originPage,
      },
    });
  };

  useEffect(() => {
    getListingData();
    // eslint-disable-next-line
  }, [searchParams, pgNo, pgSize, filterParams]);

  useEffect(() => {
    if(searchParams) {
      setPgNo(0);
    } else {
      setPgNo(Number(getStorageValue("PageNumber")));
    }
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <>
      {loading && (
        <Loaders />
      )}
      {!loading && (
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid #E4E7EC",
            maxHeight: "610px",
            minHeight: "600px",
          }}
        >
          <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {displayHeaders.map((ele) => {
                  return (
                    <>
                      {
                        <TableCell sx={{ fontSize: "13px", fontWeight: "600" }}>
                          {ele}
                        </TableCell>
                      }
                    </>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  onClick={() => {
                    handleNavigate(row.packagePin);
                  }}
                  key={row.packageId}
                  sx={{
                    height: "55px",
                    cursor: "pointer",
                    backgroundColor: idx % 2 === 0 ? "#FBFBFB" : "",
                  }}
                >
                  <TableCell
                    sx={{ fontSize: "12px", padding: "0px 14px" }}
                    component="th"
                    scope="row"
                  >
                    {row.title}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "12px", padding: "0px 14px" }}
                    component="th"
                    scope="row"
                  >
                    {row.sourceCity}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "12px", padding: "0px 14px" }}
                    component="th"
                    scope="row"
                  >
                    {row.destinationCity}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "12px", padding: "14px 14px", width: "17%", wordBreak: "break-word" }}
                    component="th"
                    scope="row"
                  >
                    {row.sender}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      padding: "0px 14px",
                      fontWeight: 500,
                    }}
                    component="th"
                    scope="row"
                  >
                    {row.weight}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "12px", padding: "0px 14px" }}
                    component="th"
                    scope="row"
                  >
                    {validateDate(row.expiryDate)}
                  </TableCell>
                  {type === 0 && (
                    <>
                      <TableCell
                        sx={{ fontSize: "12px", padding: "0px 14px" }}
                        component="th"
                        scope="row"
                      >
                        {
                          <Chip
                            sx={{
                              color: statusHolder[row?.statusId]
                                ? statusHolder[row?.statusId][0]
                                : "white",
                              backgroundColor: statusHolder[row?.statusId]
                                ? statusHolder[row?.statusId][1]
                                : "primary",
                              width: "7rem",
                            }}
                            label={
                              statusHolder[row?.statusId]
                                ? statusHolder[row?.statusId][2]
                                : "Status"
                            }
                          />
                        }
                      </TableCell>
                    </>
                  )}

                  {/* Actions */}
                  {(type === 1 || type === 5) && originPage !== "MyDelivery" ? (
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "0px",
                        paddingLeft: "8px",
                      }}
                      component="th"
                      scope="row"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div sx={{ display: "flex" }}>
                        {type !== 5 && (
                          <IconButton
                            sx={{ mr: 1, cursor: "pointer" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleCopyDetails(row.packagePin);
                            }}
                          >
                            <img
                              src={
                                require("../../assets/logos/copy_home_page.svg")
                                  .default
                              }
                              alt="deletePage"
                            />
                          </IconButton>
                        )}
                        { originPage !== "MyDelivery" && (
                          <>
                            <IconButton
                              sx={{ mr: 1, cursor: "pointer" }}
                              onClick={() =>
                                handleDeleteClick(row.packagePin, row.title)
                              }
                            >
                              <img
                                src={require("../../assets/delete.svg").default}
                                alt="deletePage"
                              />
                            </IconButton>
                            <IconButton
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                if (originPage && originPage === "MyDelivery") {
                                  navigate(`/edit-package/${row.packagePin}`, {
                                    state: {
                                      backURL: "/MyDelivery",
                                      status: type,
                                    },
                                  });
                                } else if (
                                  originPage &&
                                  originPage === "MyRequest"
                                ) {
                                  navigate(`/edit-package/${row.packagePin}`, {
                                    state: {
                                      backURL: "/MyRequest",
                                      status: type,
                                    },
                                  });
                                } else {
                                  navigate(`/edit-package/${row.packagePin}`, {
                                    state: { backURL: "/dashboard", status: type },
                                  });
                                }
                              }}
                            >
                              <img
                                src={
                                  require("../../assets/logos/edit_home_page.svg")
                                    .default
                                }
                                alt="editPage"
                              />
                            </IconButton>
                          </>
                          )}
                      </div>
                    </TableCell>
                  ) : (
                    ""
                  )}
                </TableRow>
              ))}
              {!loading && data?.length === 0 && (
                <TableRow width="100%">
                  <TableCell width="100%" colSpan={5} sx={{ padding: "10rem" }}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Modal
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: "16px",
            borderRadius: 2,
            width: "300px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#0C68F40F",
              borderRadius: "50%",
              textAlign: "center",
              marginLeft: "auto",
              marginRight: "auto",
              width: "150px",
              height: "150px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              style={{
                width: "40%",
                height: "40%",
                objectFit: "cover",
              }}
              src={deleteIcon}
              alt="delete"
            />
          </Box>
          <Box
            sx={{
              padding: "10px",
              backgroundColor: "#ffffff",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                marginBottom: "16px",
                color: "#000000",
              }}
            >
              Delete Confirmation
            </Typography>

            <Typography
              variant="body1"
              sx={{ marginBottom: "24px", color: "#555555" }}
            >
              {`Do you want to Delete the Job ${itemDetails?.title} - #${selectedPackageId}`}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <Button
                onClick={handleCancelDelete}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmDelete}
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor: "#007BFF",
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "#0056b3" },
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      {loader && (
        <Loaders />
      )}
      {!loading && (
        <Box>
          <TablePagination
            component="div"
            count={totalData}
            page={pgNo}
            onPageChange={handlePageChange}
            rowsPerPage={pgSize}
            onRowsPerPageChange={handleRowsChange}
          />
        </Box>
      )}

      <AlertMessage
        open={open}
        handleClose={handleClose}
        message={alertMessage}
        severity={alertSeverity}
      />
      <Snackbar
        open={copySuccess}
        disableWindowBlurListener
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message="Package details copied to clipboard!"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      />
    </>
  );
};

export default React.memo(PackageListingTable);
